import { createSelector } from "reselect";
import { Map } from "immutable";
import { map, slice } from "lodash";

import { AuthNormalisedEntities } from "@auth/models";
import {
    canPermissionSelectorFactory,
    levelPermissionSelectorFactory,
    getEntities as getAuthEntities,
} from "@auth/selectors";
import { getEntities, makeHydrateAlbum, makeHydrateArtist, makeHydrateTrack } from "@music/selectors";
import { MusicState, MusicNormalisedEntities } from "@music/models";
import { getEntities as getFilesEntities } from "@files/selectors";
import { FilesState, FilesNormalisedEntities } from "@files/models";
import { ChartsState, Chart, ChartRow, IChartRow } from "../models";

type State = MusicState & ChartsState & FilesState;

export const getCharts = (state: State) =>
    state.charts.items ? state.charts.items : Map<string, Chart>();
export const getChartsLastLoaded = (state: State) => state.charts.lastLoaded;

export const getChartsHydrated = createSelector(
    getCharts,
    getEntities,
    getFilesEntities,
    getAuthEntities,
    (
        charts: Map<string, Chart>,
        entities: MusicNormalisedEntities,
        files: FilesNormalisedEntities,
        auth: AuthNormalisedEntities,
    ) => {
        return charts.map(makeHydrateChart(entities, files, auth));
    },
);

const makeHydrateChart =
    (music: MusicNormalisedEntities, files: FilesNormalisedEntities, auth: AuthNormalisedEntities) =>
        (chart: Chart) => {
            const hydrateAlbum = makeHydrateAlbum(music, files, auth);
            const hydrateTrack = makeHydrateTrack(music, files, auth);
            const hydrateArtist = makeHydrateArtist(music, files, auth);
            const rows = map(chart.rows, (row: ChartRow) => {
                const row2 = {} as IChartRow;
                if (row.albumId != null && music.albums.has(row.albumId)) {
                    row2.album = hydrateAlbum(music.albums.get(row.albumId));
                }
                if (row.trackId != null && music.tracks.has(row.trackId)) {
                    row2.track = hydrateTrack(music.tracks.get(row.trackId));
                }
                if (row.artistId != null && music.artists.has(row.artistId)) {
                    row2.artist = hydrateArtist(music.artists.get(row.artistId));
                }
                return row.merge(row2) as ChartRow;
            }).slice(0, 8);
            return chart.set("rows", rows) as Chart;
        };

const chartPermissionPrefix = "wata.reporting.chart";
export const canViewChart = canPermissionSelectorFactory(chartPermissionPrefix, "view");
export const getViewChartLevel = levelPermissionSelectorFactory(chartPermissionPrefix, "view");
